import { Loader2, Sparkles } from 'lucide-react';
import type { LetterAutoReview } from '../../../lib/api';

interface AutoReviewPanelProps {
  review: LetterAutoReview | null;
  isLoading: boolean;
  isRequesting?: boolean;
  onRequestReview?: (force: boolean) => void;
}

export function AutoReviewPanel({
  review,
  isLoading,
  isRequesting = false,
  onRequestReview,
}: AutoReviewPanelProps) {
  if (isLoading) {
    return <div className="text-sm text-mist animate-pulse">Loading AI review...</div>;
  }

  if (!review) {
    return (
      <div className="bg-night border border-stone/30 rounded-xl p-4 text-sm text-mist">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span>No AI review available for this letter yet.</span>
          {onRequestReview && (
            <button
              type="button"
              onClick={() => onRequestReview(false)}
              disabled={isRequesting}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg bg-rune text-night hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isRequesting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {isRequesting ? 'Queueing...' : 'Run AI review'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-night border border-stone/30 rounded-xl p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm text-mist">AI Review</div>
          <div className="text-xl text-parchment font-medium">{review.rating}/10</div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-mist">
          <span>
            {review.provider ?? 'provider unknown'} · {review.model ?? 'model unknown'} ·{' '}
            {review.confidenceLevel}
          </span>
          {onRequestReview && (
            <button
              type="button"
              onClick={() => onRequestReview(true)}
              disabled={isRequesting}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg border border-rune/30 bg-rune/20 text-rune hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isRequesting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {isRequesting ? 'Queueing...' : 'Re-run AI review'}
            </button>
          )}
        </div>
      </div>

      <div className="text-sm text-parchment">{review.overallAssessment}</div>

      <div className="grid grid-cols-2 gap-3 text-xs text-mist">
        <div>
          Authenticity: <span className="text-parchment">{review.dimensionScores.authenticity_of_voice}</span>
        </div>
        <div>
          Emotional realism: <span className="text-parchment">{review.dimensionScores.emotional_realism}</span>
        </div>
        <div>
          Relational specificity: <span className="text-parchment">{review.dimensionScores.relational_specificity}</span>
        </div>
        <div>
          Spontaneity: <span className="text-parchment">{review.dimensionScores.spontaneity_vs_structure}</span>
        </div>
        <div>
          Language naturalness:{' '}
          <span className="text-parchment">{review.dimensionScores.language_naturalness}</span>
        </div>
        <div>
          Intent &amp; motivation:{' '}
          <span className="text-parchment">
            {review.dimensionScores.intent_motivation_plausibility}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
        <div>
          <div className="text-mist mb-1">Strengths</div>
          <ul className="space-y-1 text-parchment">
            {review.keyStrengths.map((item, idx) => (
              <li key={`${item}-${idx}`}>• {item}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-mist mb-1">Weaknesses</div>
          <ul className="space-y-1 text-parchment">
            {review.keyWeaknesses.map((item, idx) => (
              <li key={`${item}-${idx}`}>• {item}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-mist mb-1">Recommendations</div>
          <ul className="space-y-1 text-parchment">
            {review.recommendations.map((item, idx) => (
              <li key={`${item}-${idx}`}>• {item}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
